import React, { useEffect, useMemo, useState } from 'react';
import { dedupeClosingPoints, resolveFramePoints } from '../../utils/polygon';
import { buildZoneColorMap, zoneColorFrom } from '../../utils/zoneColors';
import './DetectionZonesOverlay.css';

/*
 * ТЗ V5 №6: цветные подписанные зоны поверх обработанного кадра.
 *
 * Наложение — отдельный SVG-слой над <img>, сам кадр не перерисовывается.
 * Координаты зон — пиксели original-кадра (то же пространство, что у
 * редактора зон), поэтому viewBox берётся из natural-размера original, если
 * он известен (referenceSrc), иначе из самой картинки. preserveAspectRatio
 * «meet» совпадает с object-fit: contain, так что контур не смещается при
 * resize. Слой не перехватывает клики — они уходят просмотрщику кадра.
 */

const zoneLabel = (zone) => zone?.display_name || zone?.zone_id || 'Зона';

/** Подписи у верхней вершины контура; пересекающиеся сдвигаются вниз. */
export const placeZoneLabels = (shapes, fontSize, width) => {
  const placed = [];
  return shapes.map((shape) => {
    const top = shape.points.reduce((best, point) => (
      !best || point[1] < best[1] || (point[1] === best[1] && point[0] < best[0]) ? point : best
    ), null);
    const labelWidth = shape.label.length * fontSize * 0.6;
    const x = Math.max(4, Math.min(top[0], width - labelWidth - 4));
    let y = Math.max(top[1] + fontSize * 1.2, fontSize * 1.2);
    const overlaps = (box) => placed.some((other) => (
      box.x < other.x + other.w && other.x < box.x + box.w
      && box.y - fontSize < other.y && other.y - fontSize < box.y
    ));
    for (let attempt = 0; attempt < 6 && overlaps({ x, y, w: labelWidth }); attempt += 1) {
      y += fontSize * 1.3;
    }
    placed.push({ x, y, w: labelWidth });
    return { ...shape, labelX: x, labelY: y };
  });
};

const useNaturalSize = (src) => {
  const [size, setSize] = useState(null);
  useEffect(() => {
    setSize(null);
    if (!src || typeof Image === 'undefined') return undefined;
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (!cancelled && probe.naturalWidth) setSize({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return size;
};

export const DetectionZonesOverlay = ({
  src,
  alt,
  zones = [],
  visible = true,
  highlightZoneIds = [],
  referenceSrc = '',
  block = false,
  imgClassName = '',
  imgStyle,
  onImageError,
  onImageClick,
  imgTitle,
}) => {
  const [imageSize, setImageSize] = useState(null);
  useEffect(() => { setImageSize(null); }, [src]);
  const referenceSize = useNaturalSize(referenceSrc && referenceSrc !== src ? referenceSrc : '');
  const size = referenceSize || imageSize;

  const colorMap = useMemo(() => buildZoneColorMap(zones), [zones]);
  const highlighted = useMemo(() => new Set(highlightZoneIds.map(String)), [highlightZoneIds]);

  const shapes = useMemo(() => {
    if (!size) return [];
    const fontSize = Math.max(12, Math.round(size.width / 55));
    const raw = zones
      .map((zone, index) => ({
        key: zone?.zone_id ?? `idx-${index}`,
        label: zoneLabel(zone),
        color: zoneColorFrom(colorMap, zone, index),
        linked: zone?.zone_id != null && highlighted.has(String(zone.zone_id)),
        points: resolveFramePoints(dedupeClosingPoints(zone?.polygon), size.width, size.height),
      }))
      .filter((shape) => shape.points.length >= 2);
    return placeZoneLabels(raw, fontSize, size.width).map((shape) => ({ ...shape, fontSize }));
  }, [zones, size, colorMap, highlighted]);

  const anyLinked = shapes.some((shape) => shape.linked);

  return (
    <span className={`dzo-stage${block ? ' dzo-stage--block' : ''}`}>
      <img
        src={src}
        alt={alt}
        className={imgClassName}
        style={imgStyle}
        title={imgTitle}
        onClick={onImageClick}
        onError={onImageError}
        onLoad={(event) => {
          const img = event.currentTarget;
          if (img.naturalWidth) setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
        }}
        draggable={false}
      />
      {visible && size && shapes.length > 0 && (
        <svg
          className="dzo-layer"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          data-testid="detection-zones-layer"
        >
          {shapes.map((shape) => {
            const points = shape.points.map(([x, y]) => `${x},${y}`).join(' ');
            const dimmed = anyLinked && !shape.linked;
            const strokeWidth = shape.linked ? 4 : 2;
            const Shape = shape.points.length > 2 ? 'polygon' : 'polyline';
            return (
              <g key={shape.key} opacity={dimmed ? 0.7 : 1}>
                <title>{shape.label}</title>
                <Shape
                  points={points}
                  fill="none"
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth={strokeWidth + 2}
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                />
                <Shape
                  points={points}
                  fill={Shape === 'polygon' ? shape.color.fill : 'none'}
                  stroke={shape.color.stroke}
                  strokeWidth={strokeWidth}
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                />
                <text
                  x={shape.labelX}
                  y={shape.labelY}
                  fontSize={shape.fontSize}
                  fontWeight={shape.linked ? 700 : 600}
                  className="dzo-label"
                  fill={shape.color.point}
                >
                  {shape.label}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </span>
  );
};

/**
 * Легенда и состояние слоя зон под кадром: переключатель «Показать зоны»,
 * цвет и полное название каждой зоны, честные пометки о текущих границах и
 * недоступных исторических зонах.
 */
export const DetectionZonesLegend = ({
  status,
  zones = [],
  visible = true,
  onToggle,
  highlightZoneIds = [],
  historical = false,
  snapshotZones = [],
}) => {
  const colorMap = useMemo(() => buildZoneColorMap(zones), [zones]);
  const highlighted = new Set(highlightZoneIds.map(String));
  const catalogIds = new Set(zones.map((zone) => String(zone?.zone_id)));
  // Зона из снимка события, которой больше нет в каталоге: имя есть, контура нет.
  const missing = status === 'ready'
    ? snapshotZones.filter((zone) => zone?.zone_id != null && !catalogIds.has(String(zone.zone_id)))
    : [];

  if (status === 'idle') return null;
  if (status === 'loading') return <p className="dzo-note">Загружаем зоны…</p>;
  if (status === 'error') return <p className="dzo-note">Не удалось загрузить зоны</p>;
  if (zones.length === 0 && missing.length === 0) return null;

  return (
    <div className="dzo-legend">
      {zones.length > 0 && (
        <label className="dzo-toggle">
          <input type="checkbox" checked={visible} onChange={(event) => onToggle?.(event.target.checked)} />
          Показать зоны
        </label>
      )}
      {visible && zones.length > 0 && (
        <ul className="dzo-legend-list">
          {zones.map((zone, index) => {
            const linked = zone?.zone_id != null && highlighted.has(String(zone.zone_id));
            return (
              <li
                key={zone?.zone_id ?? index}
                className={`dzo-legend-item${linked ? ' dzo-legend-item--linked' : ''}`}
                title={linked ? `${zoneLabel(zone)} · зона события` : zoneLabel(zone)}
              >
                <span
                  className="dzo-swatch"
                  style={{ background: zoneColorFrom(colorMap, zone, index).point }}
                  aria-hidden="true"
                />
                <span className="dzo-legend-name">{zoneLabel(zone)}</span>
              </li>
            );
          })}
        </ul>
      )}
      {visible && historical && zones.length > 0 && (
        <p
          className="dzo-note"
          title="Показаны границы зон на сегодня. Какими они были в момент съёмки кадра, неизвестно."
        >
          Текущие границы зон
        </p>
      )}
      {missing.map((zone) => (
        <p key={zone.zone_id} className="dzo-note">
          {zone.display_name || zone.zone_id} · Контур исторической зоны недоступен
        </p>
      ))}
    </div>
  );
};

export default DetectionZonesOverlay;
